<template>
    <DatePicker
        :class="classes"
        v-model="date"
        :locale="en"
        :showTime="showTime"
        :showIcon="isIconShown"
        dateFormat="dd.mm.yy"
        :selectionMode="selectionMode"
        :disabled="disabled"
        :name="name"
        :inline="inline"
    />
</template>

<script>
import { useUserStore } from '@/stores/user'
import DatePicker from 'primevue/datepicker'

export default {
    name: 'CoreDateInput',

    components: { DatePicker },

    props: {
        name: {
            type: String,
            default: '',
        },

        showTime: {
            type: Boolean,
            default: false,
        },

        showIcon: {
            type: Boolean,
            default: true,
        },

        selectionMode: {
            type: String,
            default: 'single',
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        validator: {
            type: Object,
            default: null,
        },

        customNotificationMessage: {
            type: Object,
            default: null,
        },

        inline: {
            type: Boolean,
            default: false,
        },

        value: {
            type: [String, Date],
            default: null,
        },
    },

    data() {
        return {
            userStore: useUserStore(),

            en: {
                firstDayOfWeek: 1,
                dayNames: [
                    'Monday',
                    'Tuesday',
                    'Wednesday',
                    'Thursday',
                    'Friday',
                    'Saturday',
                    'Sunday',
                ],
                dayNamesShort: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
                dayNamesMin: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
                monthNames: [
                    'January',
                    'February',
                    'March',
                    'April',
                    'May',
                    'June',
                    'July',
                    'August',
                    'September',
                    'October',
                    'November',
                    'December',
                ],
                monthNamesShort: [
                    'Jan',
                    'Feb',
                    'Mar',
                    'Apr',
                    'May',
                    'Jun',
                    'Jul',
                    'Aug',
                    'Sep',
                    'Oct',
                    'Nov',
                    'Dec',
                ],
                today: 'Today',
                clear: 'Clear',
                dateFormat: 'dd-mm-yy',
                weekHeader: 'Wk',
            },
        }
    },

    computed: {
        date: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit('input', value)
            },
        },

        locale() {
            const { preferred_date_format } = this.userStore.user
            return this[preferred_date_format]
        },

        isValid() {
            if (this.validator !== null && !this.validator.$invalid && this.validator.$dirty) {
                return true
            }

            return false
        },

        isIconShown() {
            if (this.inline) {
                return false
            }

            if (this.showIcon) {
                return true
            }

            return false
        },

        isInvalid() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'error'
            ) {
                return true
            }

            if (this.validator !== null && this.validator.$invalid && this.validator.$dirty) {
                return true
            }

            return false
        },

        isWarning() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'warn'
            ) {
                return true
            }

            return false
        },

        classes() {
            return {
                'core-date-input': true,
                'core-valid': this.isValid,
                'core-invalid': this.isInvalid,
                'core-warning': this.isWarning,
            }
        },
    },
}
</script>

<style>
.core-date-input {
    & > input.p-datepicker-input {
        font-size: var(--text-base);
    }

    & > button.p-datepicker-dropdown {
        background: var(--color-primary-600);
        width: 4rem;

        &:hover {
            background: var(--color-primary-500);
        }

        & > svg {
            color: var(--color-secondary);
            width: 1.5rem;
            height: 1.5rem;
        }
    }

    &.p-datepicker.core-invalid.p-component input,
    &.p-datepicker.core-invalid.p-component input:focus,
    &.p-datepicker.core-invalid.p-component input:hover {
        border: 1px solid var(--color-error);
    }

    &.p-datepicker.core-invalid.p-component > button.p-datepicker-dropdown {
        background: var(--color-error);

        &:hover {
            background: var(--color-error);
        }
    }

    &.p-datepicker.core-warning.p-component input,
    &.p-datepicker.core-warning.p-component input:focus,
    &.p-datepicker.core-warning.p-component input:hover {
        border: 1px solid var(--color-warning);
    }

    &.p-datepicker.core-warning.p-component > button.p-datepicker-dropdown {
        background: var(--color-warning);

        &:hover {
            background: var(--color-warning);
        }
    }

    &.p-datepicker.core-valid.p-component input,
    &.p-datepicker.core-valid.p-component input:focus,
    &.p-datepicker.core-valid.p-component input:hover {
        border: 1px solid var(--color-primary);
    }

    /* Direct styles for light theme to ensure application */
    .p-datepicker-panel {
        background: var(--color-neutral-100) !important;
        color: var(--text-dark) !important;
        border: none !important;
        box-shadow: none !important;
    }

    .p-datepicker-header {
        background: var(--color-neutral-100) !important;
        border-bottom: none !important;
        color: var(--text-dark) !important;
    }

    .p-datepicker-title button {
        color: var(--text-dark) !important;
    }

    .p-datepicker-title button:hover {
        background: var(--color-primary-200) !important;
        color: var(--text-dark) !important;
    }

    .p-datepicker-prev-button,
    .p-datepicker-next-button {
        background: transparent !important;
        color: var(--text-dark) !important;
    }

    .p-datepicker-prev-button:hover,
    .p-datepicker-next-button:hover {
        background: var(--color-neutral-500) !important;
        color: var(--text-dark) !important;
    }

    .p-datepicker-day-view th {
        background: var(--color-neutral-100) !important;
        color: var(--text-dark) !important;
        border: none !important;
    }

    .p-datepicker-weekday {
        color: var(--text-dark) !important;
    }

    .p-datepicker-day-cell {
        background: var(--color-neutral-100) !important;
    }

    .p-datepicker-day {
        color: var(--text-dark) !important;
        border: 2px solid transparent !important;
        border-radius: 50% !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 3rem !important;
        height: 3rem !important;
    }

    .p-datepicker-other-month .p-datepicker-day {
        color: var(--color-neutral-900) !important;
    }

    .p-datepicker-today .p-datepicker-day {
        background: transparent !important;
        border-color: var(--color-primary-700) !important;
        color: var(--text-dark) !important;
    }

    td:hover .p-datepicker-day {
        background: var(--color-neutral-500) !important;
        color: var(--text-dark) !important;
    }

    .p-highlight .p-datepicker-day {
        background: var(--color-primary-200) !important;
        color: var(--color-primary-700) !important;
    }

    td.p-highlight:hover .p-datepicker-day {
        background: var(--color-primary-300) !important;
        color: var(--color-primary-700) !important;
    }

    /* For range selection */
    .p-datepicker-day.p-datepicker-in-range {
        background: var(--color-primary-200) !important;
        color: var(--text-dark) !important;
    }

    /* Styles for month picker */
    .p-datepicker-month {
        color: var(--text-dark) !important;
    }

    .p-datepicker-month:hover {
        background: var(--color-primary-200) !important;
        color: var(--text-dark) !important;
    }

    .p-datepicker-month.p-datepicker-month-selected {
        background: var(--color-primary-200) !important;
        color: var(--color-primary-700) !important;
    }

    /* Styles for year picker */
    .p-datepicker-year {
        color: var(--text-dark) !important;
    }

    .p-datepicker-year:hover {
        background: var(--color-primary-200) !important;
        color: var(--text-dark) !important;
    }

    .p-datepicker-year.p-datepicker-year-selected {
        background: var(--color-primary-200) !important;
        color: var(--color-primary-700) !important;
    }

    .p-datepicker-decade {
        color: var(--text-dark) !important;
    }

    /* Increase size for views */
    .p-datepicker-day-view,
    .p-datepicker-month-view,
    .p-datepicker-year-view {
        font-size: 1em !important;
    }

    /* Override PrimeVue CSS variables for light theme using app variables as fallback */
    --text-color: var(--text-dark); /* Main text color */
    --text-secondary-color: var(--color-neutral-900); /* Secondary/muted text */
    --primary-color: var(--color-primary-700); /* Primary accent (e.g., selected date highlight) */
    --primary-color-text: var(--text-light); /* Text on primary backgrounds */
    --surface-ground: var(--color-neutral-300); /* Base surface */
    --surface-section: var(--color-neutral-100); /* Section backgrounds */
    --surface-card: var(--color-neutral-100); /* Card-like surfaces (calendar body) */
    --surface-overlay: var(--color-neutral-100); /* Overlay surfaces (calendar panel) */
    --surface-border: var(--color-neutral-700); /* Borders and dividers */
    --surface-hover: var(--color-neutral-500); /* Hover states */
    --highlight-bg: var(--color-primary-200); /* Highlight background (e.g., today/selected) */
    --highlight-text-color: var(--color-primary-700); /* Highlight text */
    --border-radius: 6px; /* Rounded corners */
    --focus-ring: 0 0 0 0.2rem var(--color-primary-300); /* Focus outline */
    --mask-bg: rgba(0, 0, 0, 0.4); /* Any overlay masks, if applicable */

    /* Additional DatePicker-specific overrides to ensure coverage */
    --p-content-bg: var(--color-neutral-100); /* Calendar content background */
    --p-content-border-color: var(--color-neutral-700); /* Content borders */
    --p-datepicker-header-bg: var(--color-neutral-200); /* Header background */
    --p-datepicker-header-text-color: var(--text-dark); /* Header text */
    --p-datepicker-cell-hover-bg: var(--color-neutral-500); /* Cell hover */
    --p-datepicker-cell-selected-bg: var(--color-primary-700); /* Selected cell bg */
    --p-datepicker-cell-selected-text-color: var(--text-light); /* Selected text */
    --p-datepicker-today-bg: var(--color-primary-200); /* Today highlight */
    --p-datepicker-today-text-color: var(--text-dark); /* Today text */
    --p-datepicker-outside-month-text-color: var(--color-neutral-900); /* Dates from other months */
}

.core-date-input > input:disabled {
    background-color: var(--color-disabled-bg);
    color: var(--color-neutral-900);
}

.core-date-input:has(input:disabled) > button,
.core-date-input:has(input:disabled) > button:hover,
.core-date-input:has(input:disabled) > button:focus {
    background-color: var(--color-disabled-bg);
    cursor: default;
}
</style>